import { formatCount, tt } from '@agos/shared'

/**
 * The Control Tower's franchise list — `/franchises`.
 *
 * Phase M's last two pages, unblocked on 2026-07-31 by the owner choosing a seeded **development**
 * super-admin on the local stack (`scripts/dev-super-admin.sh`). The RS256 asymmetry is untouched: the
 * account signs in through `POST /central/auth/login` and the platform mints its own tower token, which
 * is what `TowerJwtSigner` says nothing inside a tenant may do for itself.
 *
 * **Two things the spec assumed from reading turned out otherwise, and both matter.**
 *
 * 1. `GET /super-admin/franchises` answers an **envelope** — `{"franchises": [...]}` — not a bare array.
 *    A client that read it as a list would render nothing and look like a franchise-less platform.
 * 2. Every row carries **32 fields**, and the sensitive ones are already handled the right way:
 *    `databaseUrl` arrives with its password replaced by `***`, and the master API key and WireGuard
 *    config are reported as **presence booleans** (`hasMasterApiKey`, `hasWireguardConfig`) rather than
 *    values. So this screen has nothing to strip — which is worth stating, because FB-132, FB-138 and
 *    FB-143 are all the same endpoint shape done the other way.
 *
 * **Measured, and it confirms what the spec predicted from the database:** 3 franchises, all active,
 * with 2 / 1 / **0** domains. Franchise 8 has none, so it has no customer site and the branch registry
 * correctly refuses to offer it.
 */

export interface FranchiseDomain {
  domain: string
  isPrimary: boolean
}

export interface Franchise {
  id: number
  name: string
  slug: string
  isActive: boolean
  domains: FranchiseDomain[]
  packageName: string | null
  /** Present or absent, never the value itself. */
  hasMasterApiKey: boolean
  hasWireguardConfig: boolean
  /** The password is already `***` when it arrives. */
  databaseUrl: string | null
  createdAt: string | null
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null
}

/** Reads the envelope. A bare array is accepted too, so a shape change does not blank the screen. */
export function franchisesOf(payload: unknown): Franchise[] {
  const rows = Array.isArray(payload)
    ? payload
    : ((payload as { franchises?: unknown })?.franchises ?? [])
  if (!Array.isArray(rows)) return []
  return rows.flatMap((entry) => {
    const row = entry as Record<string, unknown>
    if (typeof row.id !== 'number') return []
    const domains = Array.isArray(row.domains) ? row.domains : []
    const pkg = row.package as { name?: unknown } | null | undefined
    return [
      {
        id: row.id,
        name: text(row.name) ?? String(row.id),
        slug: text(row.slug) ?? '',
        isActive: row.isActive !== false,
        domains: domains.flatMap((entry2) => {
          const domain = entry2 as Record<string, unknown>
          const name = text(domain.domain)
          return name === null ? [] : [{ domain: name, isPrimary: domain.isPrimary === true }]
        }),
        packageName: text(pkg?.name),
        hasMasterApiKey: row.hasMasterApiKey === true,
        hasWireguardConfig: row.hasWireguardConfig === true,
        databaseUrl: text(row.databaseUrl),
        createdAt: text(row.createdAt),
      },
    ]
  })
}

export function primaryDomainOf(franchise: Franchise): string | null {
  return (franchise.domains.find((domain) => domain.isPrimary) ?? franchise.domains[0])?.domain ?? null
}

/**
 * Said on a row with no domain at all.
 *
 * Franchise 8 is in this state. It is active, it appears in every list, and it has no customer site —
 * `central/branches` correctly refuses to offer it. A blank cell reads as *not loaded*; this says which.
 */
export function noDomainNote(): string {
  return tt('Bez domény - táto pobočka nemá zákaznícku stránku a v prepínači sa neponúka.')
}

/** Said once above the list, because a masked secret is worth noticing rather than assuming. */
export function secretsNote(): string {
  return tt(
    'Heslo k databáze prichádza už zamaskované a kľúče sa hlásia len ako prítomné či chýbajúce - žiadna hodnota sa sem neposiela.',
  )
}

// ─── deactivating, and the thing that is not deactivating ────────────────────

/**
 * The two `DELETE` shapes, which legacy hid behind one boolean — FB-P01.
 *
 * `deleteFranchise(f, force)` decided between *deactivate* and *erase from the central registry* with a
 * boolean, sent both to the same endpoint, and asked about both in a `window.confirm` whose destructive
 * branch was half English. Worse, its reassurance was the frightening part: *"Databáza zostane!"* — the
 * tenant database survives and the registry row does not, so the franchise's data goes on existing with
 * nothing pointing at it. No domain resolution, no tenant routing, no way back through this screen.
 */
export type RemoveKind = 'deactivate' | 'purge'


export function removeTitle(kind: RemoveKind): string {
  return kind === 'purge' ? tt('Vymazať z centrálneho registra?') : tt('Deaktivovať pobočku?')
}

export function removeDescription(kind: RemoveKind, franchise: Franchise): string {
  if (kind === 'deactivate') {
    return `${franchise.name} - ${tt('prestane byť aktívna. Dá sa to vrátiť.')}`
  }
  return `${franchise.name} - ${tt('zmizne z centrálneho registra. Databáza pobočky zostane na serveri, ale nebude na ňu ukazovať nič: žiadna doména, žiadne smerovanie, a cez túto obrazovku sa už nevráti. Toto sa vrátiť nedá.')}`
}

/** A purge is typed out, the way every other irreversible write in this port is. */
export function purgeConfirmation(franchise: Franchise): string {
  return franchise.slug === '' ? String(franchise.id) : franchise.slug
}

export function purgeIsArmed(franchise: Franchise, typed: string): boolean {
  return typed.trim() === purgeConfirmation(franchise)
}

export function countsNote(franchises: readonly Franchise[]): string {
  const active = franchises.filter((franchise) => franchise.isActive).length
  const domainless = franchises.filter((franchise) => franchise.domains.length === 0).length
  const parts = [`${formatCount(franchises.length)} ${tt('pobočiek')}`]
  if (active !== franchises.length) parts.push(`${formatCount(active)} ${tt('aktívnych')}`)
  if (domainless > 0) parts.push(`${formatCount(domainless)} ${tt('bez domény')}`)
  return parts.join(' · ')
}
